import { useMemo, useCallback, useState, useRef, useEffect } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import {
    AUDITED_TYPE_CONSOLIDATED,
    AUDITED_TYPE_STANDALONE,
    FINANCIAL_NATURE_QUERY_PARAM,
} from '../../../../utils/constants/commonConstants';
import { updateQueryParams } from '../../../../utils/searchParamUtils';
import {
    deriveAuditedType,
    FinancialNature,
    natureToAuditedType,
    parseFinancialNatureParam,
    pickEffectiveAuditedType,
} from './auditedTypeUtils';

/**
 * One Standalone/Consolidated selection per company, shared by every surface through the
 * `financialNature` URL param (absent = derived default, never written).
 *
 * Lifecycle:
 * 1. Consumer fetches data → response carries `latestConsolidatedFY` / `latestStandaloneFY`.
 * 2. Consumer calls `setAuditedTypeFromYears(consolidatedFY, standaloneFY, provisionalPair?)`.
 *    The surface's availability is the provisional pair when given, else the audited pair.
 * 3. Selection = valid URL param, else the default derived from the audited pair (then the provisional pair).
 *    `auditedType` is the selection when the surface has that nature, else the nature it does have.
 * 4. `onConsolidationChange(nature)` fires whenever the effective nature differs from the last one
 *    this instance requested (`getRequestNature()` records it), so every instance on the page catches up.
 *
 * @param onConsolidationChange — called with the uppercase nature string to re-fetch data.
 * @param resetKey — when this value changes (e.g. orgId), the surface's pairs are cleared.
 * @param fallbackType — the selection when the URL has none, e.g. the type the page behind a drawer shows.
 */

type AuditedTypeOption = { misDisplayName: string; isDataAvailable: boolean };
type FYPair = { consolidatedFY: number | null; standaloneFY: number | null };
type FYPairs = { audited: FYPair; provisional: FYPair | null };

const EMPTY_PAIRS: FYPairs = { audited: { consolidatedFY: null, standaloneFY: null }, provisional: null };

interface UseAuditedTypeReturn {
    auditedType: string | null;
    selectedNature: string | undefined;
    latestConsolidatedFY: number | null;
    latestStandaloneFY: number | null;
    auditedTypeOptionsObject: AuditedTypeOption[];
    handleConsolidationChange: (val: string) => void;
    setAuditedTypeFromYears: (consolidatedFY: number | null, standaloneFY: number | null, provisional?: FYPair) => void;
    getRequestNature: () => string | undefined;
    requestNature: (type: string) => string | undefined;
}

/** Convert a display value (e.g. 'Consolidated') to the API nature string ('CONSOLIDATED'). */
export const toNature = (type: string | null): string | undefined => type?.toUpperCase() ?? undefined;

function resolveSurfaceTypes(urlType: string | null, pairs: FYPairs) {
    const surface = pairs.provisional ?? pairs.audited;
    const selected =
        urlType ??
        deriveAuditedType(pairs.audited.consolidatedFY, pairs.audited.standaloneFY) ??
        (pairs.provisional ? deriveAuditedType(pairs.provisional.consolidatedFY, pairs.provisional.standaloneFY) : null);
    return { surface, selected, effective: pickEffectiveAuditedType(selected, surface.consolidatedFY, surface.standaloneFY) };
}

export function useAuditedType(
    onConsolidationChange?: (nature: string) => void,
    resetKey?: string | number,
    fallbackType?: string | null,
): UseAuditedTypeReturn {
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    // A nature this instance requested without data to validate it, e.g. a retry after a failed fetch.
    const [requestedNature, setRequestedNature] = useState<FinancialNature>();
    const urlNature =
        parseFinancialNatureParam(searchParams.get(FINANCIAL_NATURE_QUERY_PARAM)) ??
        requestedNature ??
        parseFinancialNatureParam(fallbackType ?? null);
    const urlType = urlNature ? natureToAuditedType(urlNature) : null;

    const [pairs, setPairs] = useState<FYPairs>(EMPTY_PAIRS);
    // Refs mirror the latest values so the request-time getter and the stable setter never read stale state.
    const pairsRef = useRef<FYPairs>(EMPTY_PAIRS);
    const urlTypeRef = useRef(urlType);
    urlTypeRef.current = urlType;
    const onConsolidationChangeRef = useRef(onConsolidationChange);
    onConsolidationChangeRef.current = onConsolidationChange;
    // undefined = the last request carried no nature, which counts as the pair's derived default.
    const lastRequestedNatureRef = useRef<string | undefined>(undefined);

    useEffect(() => {
        if (resetKey === undefined) return;
        pairsRef.current = EMPTY_PAIRS;
        lastRequestedNatureRef.current = undefined;
        setPairs(EMPTY_PAIRS);
    }, [resetKey]);

    const { surface, selected, effective } = resolveSurfaceTypes(urlType, pairs);
    const effectiveNature = toNature(effective);
    const selectedNature = toNature(selected);

    const auditedTypeOptionsObject = useMemo<AuditedTypeOption[]>(
        () => [
            { misDisplayName: AUDITED_TYPE_CONSOLIDATED, isDataAvailable: surface.consolidatedFY !== null },
            { misDisplayName: AUDITED_TYPE_STANDALONE, isDataAvailable: surface.standaloneFY !== null },
        ],
        [surface.consolidatedFY, surface.standaloneFY],
    );

    const setAuditedTypeFromYears = useCallback((consolidatedFY: number | null, standaloneFY: number | null, provisional?: FYPair) => {
        const prev = pairsRef.current;
        const isUnchanged =
            prev.audited.consolidatedFY === consolidatedFY &&
            prev.audited.standaloneFY === standaloneFY &&
            prev.provisional?.consolidatedFY === provisional?.consolidatedFY &&
            prev.provisional?.standaloneFY === provisional?.standaloneFY;
        // Polling re-delivers the same pair; skipping it avoids a re-render per tick.
        if (isUnchanged) return;
        const next: FYPairs = { audited: { consolidatedFY, standaloneFY }, provisional: provisional ?? null };
        pairsRef.current = next;
        setPairs(next);
    }, []);

    const getRequestNature = useCallback(() => {
        const { selected: currentSelected, effective: currentEffective } = resolveSurfaceTypes(urlTypeRef.current, pairsRef.current);
        const nature = toNature(currentEffective ?? currentSelected);
        lastRequestedNatureRef.current = nature;
        return nature;
    }, []);

    const requestNature = useCallback((type: string) => {
        const nature = parseFinancialNatureParam(type) ?? undefined;
        lastRequestedNatureRef.current = nature;
        setRequestedNature(nature);
        return nature;
    }, []);

    useEffect(() => {
        // With no data in either nature the selection itself is requested, once a first request has been made.
        const target = effectiveNature ?? (lastRequestedNatureRef.current !== undefined ? selectedNature : undefined);
        if (!target) return;
        const { surface: currentSurface } = resolveSurfaceTypes(urlTypeRef.current, pairsRef.current);
        const lastRequested =
            lastRequestedNatureRef.current ?? toNature(deriveAuditedType(currentSurface.consolidatedFY, currentSurface.standaloneFY));
        if (target === lastRequested) return;
        lastRequestedNatureRef.current = target;
        onConsolidationChangeRef.current?.(target);
    }, [effectiveNature, selectedNature]);

    const handleConsolidationChange = useCallback(
        (val: string) => {
            if (val === effective) return;
            const isAvailable = val === AUDITED_TYPE_CONSOLIDATED ? surface.consolidatedFY !== null : surface.standaloneFY !== null;
            if (!isAvailable) return;
            updateQueryParams(searchParams, setSearchParams, { [FINANCIAL_NATURE_QUERY_PARAM]: toNature(val) }, location);
        },
        [effective, surface.consolidatedFY, surface.standaloneFY, searchParams, setSearchParams, location],
    );

    return {
        auditedType: effective,
        selectedNature: toNature(selected),
        latestConsolidatedFY: surface.consolidatedFY,
        latestStandaloneFY: surface.standaloneFY,
        auditedTypeOptionsObject,
        handleConsolidationChange,
        setAuditedTypeFromYears,
        getRequestNature,
        requestNature,
    };
}

/**
 * Builds the Standalone/Consolidated `CategorySelector` segmented-toggle options, greying out
 * whichever nature has no data. Shared by AuditedFinancialsVisualisation, AuditorCommentsDrawer,
 * and FinancialMetrics so the three surfaces can't drift on this logic.
 */
export const buildConsolidationCategoryOptions = (auditedTypeOptionsObject: AuditedTypeOption[]) =>
    [AUDITED_TYPE_STANDALONE, AUDITED_TYPE_CONSOLIDATED].map((type) => ({
        id: type,
        label: type,
        disabled: !(auditedTypeOptionsObject.find((opt) => opt.misDisplayName === type)?.isDataAvailable ?? false),
    }));
